import { DestroyRef, Directive, ElementRef, inject, input, OnInit } from '@angular/core';

/**
 * Measures the host element's height via `ResizeObserver` and writes it as a
 * CSS custom property on the nearest ancestor matching a selector (or the host
 * itself as fallback), so siblings/descendants can size against it in CSS.
 *
 * Inputs:
 * - `pxcObserveHeightAsVar`: name of the CSS variable to set (e.g. `--row-h`).
 * - `pxcObserveHeightAsVarTarget`: CSS selector for the ancestor on which the
 *   variable is set, resolved via `closest()`.
 */
@Directive({
  selector: '[pxcObserveHeightAsVar]',
})
export class ObserveHeightAsVarDirective implements OnInit {
  readonly pxcObserveHeightAsVar = input.required<string>();
  readonly pxcObserveHeightAsVarTarget = input.required<string>();

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    const target = this.el.nativeElement.closest(this.pxcObserveHeightAsVarTarget()) ?? this.el.nativeElement;
    const update = (): void => {
      const height = this.el.nativeElement.getBoundingClientRect().height;
      (target as HTMLElement).style.setProperty(this.pxcObserveHeightAsVar(), `${height}px`);
    };
    const observer = new ResizeObserver(update);
    observer.observe(this.el.nativeElement);
    update();
    this.destroyRef.onDestroy(() => observer.disconnect());
  }
}
